import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaTimes, FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaLightbulb, FaMoon } from "react-icons/fa";
import Nav from './Nav';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { Features } from '../../assets/features/Features';
import LanguageSwitcher from '../../elements/switcher/LanguageSwitcher';
import InfoButton from '../../elements/info-button/InfoButton';

const MobileMenu = ({MobileHandler}) => {
    const { language } = useLanguage();
    const { theme, toggleTheme } = useTheme();
    const menuRef = useRef(null);
    
    // Determine mobile menu logo based on theme
    const mobileLogo = theme === 'dark' ? "/images/1.png" : "/images/3.png";

    // Handle click outside to close menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            const mobileMenuPopup = document.querySelector('.mobilemenu-popup');
            if (mobileMenuPopup && mobileMenuPopup.classList.contains('show')) {
                // Check if click is on the backdrop (the mobilemenu-popup itself, not the inner content)
                if (event.target === mobileMenuPopup || 
                    (menuRef.current && !menuRef.current.contains(event.target))) {
                    // Check if click is not on the mobile menu button
                    const mobileMenuBtn = document.querySelector('.mobile-menu-btn');
                    if (!mobileMenuBtn || !mobileMenuBtn.contains(event.target)) {
                        MobileHandler();
                    }
                }
            }
        };

        // Use both mousedown and click for better compatibility
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [MobileHandler]);

    // Fix mobile menu navigation to display in column
    useEffect(() => {
        const fixMobileMenuLayout = () => {
            const mobileMenuPopup = document.querySelector('.mobilemenu-popup');
            if (mobileMenuPopup && mobileMenuPopup.classList.contains('show')) {
                const mainmenu = mobileMenuPopup.querySelector('.mainmenu-nav .mainmenu');
                if (mainmenu) {
                    // Directly override inline styles
                    mainmenu.style.display = 'flex';
                    mainmenu.style.flexDirection = 'column';
                    mainmenu.style.alignItems = 'flex-start';
                    
                    const menuItems = mainmenu.querySelectorAll('li');
                    menuItems.forEach(li => {
                        li.style.margin = '10px 0';
                        li.style.width = '100%';
                        const link = li.querySelector('a');
                        if (link) {
                            link.style.display = 'block';
                            link.style.width = '100%';
                            link.style.padding = '10px 0';
                        }
                    });
                }
            }
        };

        // Run when menu opens
        const observer = new MutationObserver(() => {
            setTimeout(fixMobileMenuLayout, 10);
        });

        const mobileMenuPopup = document.querySelector('.mobilemenu-popup');
        if (mobileMenuPopup) {
            observer.observe(mobileMenuPopup, {
                attributes: true,
                attributeFilter: ['class'],
                childList: true,
                subtree: true
            });
            // Also check periodically when menu is open
            const interval = setInterval(() => {
                if (mobileMenuPopup.classList.contains('show')) {
                    fixMobileMenuLayout();
                }
            }, 100);
            
            return () => {
                observer.disconnect();
                clearInterval(interval);
            };
        }
    }, []);

    return (
        <div className="mobilemenu-popup" ref={menuRef}>
            <div className="mobilemenu-inner">
                <div className="mobilemenu-header">
                    <div className="mobile-nav-logo">
                        <Link to="/">
                            <img src={process.env.PUBLIC_URL + mobileLogo} alt="Site Logo" style={{maxHeight: '50px', width: 'auto'}} />
                        </Link>
                    </div>
                    <button className="mobile-menu-close" onClick={MobileHandler} ><FaTimes /></button>
                </div>
                <div className="mobilemenu-body">
                    <Nav />
                    
                    {/* Language and Theme Switchers */}
                    <div className="mobile-switchers" style={{marginTop: '30px', paddingTop: '30px', borderTop: '1px solid rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'flex-start'}}>
                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '15px', width: '100%'}}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                <span style={{fontSize: '14px', fontWeight: '500', color: 'var(--color-body)'}}>
                                    {language === 'pt' ? 'Tema' : 'Theme'}:
                                </span>
                                <button 
                                    onClick={toggleTheme}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid var(--color-body)',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        color: 'var(--color-body)',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <span style={{display: theme === 'dark' ? 'none' : 'block'}}>
                                        <FaMoon />
                                    </span>
                                    <span style={{display: theme === 'dark' ? 'block' : 'none'}}>
                                        <FaLightbulb />
                                    </span>
                                </button>
                            </div>
                            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                <span style={{fontSize: '14px', fontWeight: '500', color: 'var(--color-body)'}}>
                                    {language === 'pt' ? 'Idioma' : 'Language'}:
                                </span>
                                <LanguageSwitcher />
                            </div>
                        </div>
                    </div>
                    
                    {/* Contact Information */}
                    <div className="mobile-contact-info" style={{marginTop: '30px', paddingTop: '30px', borderTop: '1px solid rgba(0,0,0,0.1)'}}>
                        <h6 style={{fontSize: '16px', fontWeight: '600', marginBottom: '20px', color: 'var(--color-dark)'}}>{Features.COMMON.CONTACTS_LABEL[language]}</h6>
                        <ul className="list-unstyled" style={{margin: 0}}>
                            <li style={{marginBottom: '15px', display: 'flex', alignItems: 'flex-start'}}>
                                <FaMapMarkerAlt style={{marginRight: '10px', marginTop: '4px', flexShrink: 0, color: 'var(--color-primary)'}} />
                                <span style={{fontSize: '14px', lineHeight: '1.6'}}>
                                    <strong>{Features.COMMON.COMPANY_NAME[language]}</strong><br />
                                    {Features.COMMON.ADDRESS[language]}<br />
                                    {Features.COMMON.POSTAL_CODE[language]}
                                </span>
                            </li>
                            <li style={{marginBottom: '15px', display: 'flex', alignItems: 'center'}}>
                                <FaPhone style={{marginRight: '10px', flexShrink: 0, color: 'var(--color-primary)'}} />
                                <a href="tel:+351914440333" style={{fontSize: '14px', color: 'var(--color-body)', textDecoration: 'none'}}>
                                    (+351) 914 440 333
                                </a>
                                <InfoButton />
                            </li>
                            <li style={{marginBottom: '15px', display: 'flex', alignItems: 'center'}}>
                                <FaEnvelope style={{marginRight: '10px', flexShrink: 0, color: 'var(--color-primary)'}} />
                                <a href="mailto:geral@fielcarga.pt" style={{fontSize: '14px', color: 'var(--color-body)', textDecoration: 'none'}}>
                                    geral@fielcarga.pt
                                </a>
                            </li>
                            <li style={{marginBottom: '15px', display: 'flex', alignItems: 'center'}}>
                                <FaClock style={{marginRight: '10px', flexShrink: 0, color: 'var(--color-primary)'}} />
                                <span style={{fontSize: '14px'}}>{Features.FOOTER.HOURS[language]}</span>
                            </li>
                        </ul>
                    </div>
                    
                    <style jsx>{`
                        :global(.mobilemenu-popup .mainmenu-nav) {
                            display: block !important;
                        }
                        :global(.mobilemenu-popup .mainmenu-nav ul.mainmenu) {
                            display: flex !important;
                            flex-direction: column !important;
                            align-items: flex-start !important;
                            flex-wrap: nowrap !important;
                        }
                        :global(.mobilemenu-popup .mainmenu-nav ul.mainmenu[style]) {
                            display: flex !important;
                            flex-direction: column !important;
                            align-items: flex-start !important;
                        }
                        :global(.mobilemenu-popup .mainmenu-nav .mainmenu li) {
                            margin: 10px 0 !important;
                            margin-left: 0 !important;
                            margin-right: 0 !important;
                            width: 100% !important;
                        }
                        :global(.mobilemenu-popup .mainmenu-nav .mainmenu li[style]) {
                            margin: 10px 0 !important;
                            margin-left: 0 !important;
                            margin-right: 0 !important;
                        }
                        :global(.mobilemenu-popup .mainmenu-nav .mainmenu li a) {
                            display: block !important;
                            width: 100% !important;
                            padding: 10px 0 !important;
                        }
                        :global(body.active-dark-mode .mobile-contact-info) {
                            border-top-color: rgba(255,255,255,0.1);
                        }
                        :global(body.active-dark-mode .mobile-contact-info h6) {
                            color: var(--color-white);
                        }
                        :global(body.active-dark-mode .mobile-contact-info a),
                        :global(body.active-dark-mode .mobile-contact-info span) {
                            color: var(--color-body);
                        }
                        .mobile-switchers button:hover {
                            background-color: var(--color-primary) !important;
                            border-color: var(--color-primary) !important;
                            color: #ffffff !important;
                        }
                        :global(body.active-dark-mode .mobile-switchers) {
                            border-top-color: rgba(255,255,255,0.1);
                        }
                        :global(body.active-dark-mode .mobile-switchers span) {
                            color: var(--color-body);
                        }
                    `}</style>
                </div>
            </div>
        </div>
    )
}

export default MobileMenu;